import {
    Compiler,
    Component,
    ComponentRef,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ReflectiveInjector,
    ResolvedReflectiveProvider,
    SimpleChanges,
    SystemJsNgModuleLoader,
    ViewChild,
    ViewContainerRef,
    ViewRef
} from "@angular/core";
import {Observable} from "rxjs";
import {TCITenantRemoteServices} from "./tci-tenant-remote-services";
import {
    ITCITenantComponentInput,
    ITCITenantComponentOutput,
    TCITenantAppType,
    TCITenantSelectorType
} from "@tci/common";
import {TciTenantModelService} from "./tci-tenant.model";
import {TciTenantService} from "./tci-tenant.service";
import {
    ITCIResFactory,
    ITCITenantCompCacheService,
    TciTenantComponentCacheService,
    ITCIComponentCacheLoader
} from "./tci-tenant-component-cache.service";
import {
    AbstractTCITenantComponent,
    ITCITenantComponentLoaderMetadata,
    TCI_TENANT_COMPONENT_LOADER_SYMBOL
} from "./tci-tenant-component";

declare var Reflect;

// tslint:disable:no-empty-interface
// tslint:disable:component-class-suffix
// tslint:disable:max-line-length

/**
 * The [[TciTenant]] being the abstract base class is extended  by a tenant provider
 * The Tenant provider provides @TciTenant({}) annotation based on the @ITCITenantMetaData.
 * The annotation describes the tenant type and an array of Function providers
 * Each function provider has its own interface for input and output
 *  ### Example [[TciTenant]]  Typescript file
 *  ```
 *  import {Injectable, Injector} from "@angular/core";
 *  import {Observable} from "rxjs";
 *  import {TciTenant} from "@tci/core";
 *  import {Http} from "@angular/http";
 *  @TCITenantComponent(
 *      isDynamic: false,
 *      tenantAppType: TCI_TENANT_APP_TYPE.flogo,
 *      moduleRoute: "/path/to/the/tenant/module.ts";
 *      moduleClass: "TenantComponent";
 *      componentSelector: "tenant-cmp";
 *   )
 *  @Component({
 *   selector: "tenant-cmp",
 *   moduleId: module.id,
 *   template: "<tenant></tenant>"
 *  })
 * export default class TenantComponent extends AbstractTCITenantComponent {
 *      public onTenantInit() {}
 *      public onTenantDestroy() {}
 * }
 *
 *  ```
 *  This tenant needs to be exposed from the `${tenant-name}-svc` entry point module as
 *  @NgModule({
 *   imports: [
 *       HttpModule
 *   ],
 *   exports: [
 *
 *   ],
 * })
 * export default class TenantModule {
 * }
 *
 * This class is extended on the web-server side
 */
@Component({
    selector: "ng-remote-cmp",
    template: "<template #dynamic></template>"
})
export abstract class AbstractTciTenantComponentLoader<E, I extends ITCITenantComponentInput, O extends ITCITenantComponentOutput<E>>
    implements OnInit, OnDestroy, OnChanges {

    @Input() public input: I;
    @Output() public output: O = <any>new EventEmitter<ITCITenantComponentOutput<E>>();

    @Output() private componentMounted: EventEmitter<boolean> = new EventEmitter();
    @ViewChild("dynamic", {read: ViewContainerRef}) viewref: ViewContainerRef;
    private resolvedProviders: ResolvedReflectiveProvider[];
    private componentInjector: ReflectiveInjector;
    private cmpRef: ComponentRef<any>;
    private tenantCompCacheService: ITCIComponentCacheLoader;

    /**
     * @param loader
     * @param compiler
     * @param tenantRemoteServices
     * @param tenantSvc
     * @param tenantCompCacheService
     */
    constructor(
        private loader: SystemJsNgModuleLoader,
        private compiler: Compiler,
        private tenantRemoteServices: TCITenantRemoteServices,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        tenantCompCacheService: ITCITenantCompCacheService = null) {
        if (!tenantCompCacheService) {
            this.tenantCompCacheService = TciTenantComponentCacheService.getInstance(
                loader, compiler, tenantRemoteServices, tenantSvc, tenantModelSvc) as any;
        }
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges) {
        this.init();
    }

    private init() {
        this.viewref.clear();
        if (this.input) {
            this.tenantSvc.onInit()
                .map(() => {
                    this.resolvedProviders = ReflectiveInjector.resolve([
                        {
                            provide: Injector,
                            useValue: this.viewref.parentInjector
                        },
                        {
                            provide: "input",
                            useValue: this.input
                        },
                        {
                            provide: "output",
                            useValue: this.output
                        }
                    ]);
                    this.componentInjector = ReflectiveInjector.fromResolvedProviders(
                        this.resolvedProviders,
                        this.viewref.parentInjector
                    );
                    return null;
                })
                .map(() => this.getDynamic() ? this.loadDynamicComponent() : this.loadComponent())
                .flatMap(res => res)
                .subscribe(
                    res => this.componentMounted.emit(true),
                    err => {
                        this.componentMounted.emit(false);
                        // console.warn("Error loading dynamic component:", this.input, err.message, err);
                    }
                );
        }
    }

    public tenantOnInit(): Observable<any> {
        return Observable.empty();
    }

    ngOnDestroy(): void {
        this.tenantOnDestroy()
            .subscribe(res => {
            }, err => {
                console.error(err);
            }, () => {
            });
    }

    public tenantOnDestroy(): Observable<any> {
        return Observable.empty();
    }

    /**
     * The tenant end points are searched for matching selectors and tenant app types to get the right component
     * based on @TCITenantComponent annotation or @Input interface
     * @return @Observable<@ViewRef>
     */
    private loadDynamicComponent(): Observable<ViewRef> {
        const mountView = (data: ITCIResFactory<any, any>) => {
            try {
                const modRef = data.modFactory.create(this.getInjector());
                this.cmpRef = this.viewref.createComponent(data.cmpFactory, 0, modRef.injector);
                const thisInst: AbstractTCITenantComponent<E, I, O> = this.cmpRef.instance;
                thisInst.input = this.input;
                thisInst.output = this.output;
                return Observable.of(this.viewref.insert(this.cmpRef.hostView));
            } catch (e) {
                console.error("Component mount failed: ", e);
                return Observable.throw(e);
            }
        };

        const selectorType = this.getComponentSelector();
        const tenantType = this.getTenantAppType();
        const tenants = tenantType.toString().split(",");
        if (tenants.length > 1) {
            return Observable.of(...tenants)
                .map(tenantname => TCITenantAppType.fromString(tenantname))
                .map(tenant => this.tenantCompCacheService.getComponentFromCache(tenant, selectorType))
                .filter(data => !!data)
                .distinct(data => {
                    if (data.distinctKeyFn) {
                        return data.distinctKeyFn(this.input);
                    } else {
                        return data;
                    }
                })
                .flatMap(data => mountView(data));

        } else {
            const data = this.tenantCompCacheService.getComponentFromCache(tenantType, selectorType);
            if (data) {
                return mountView(data);
            } else {
                return Observable.throw("Requested component is not available");
            }
        }

    }


    /**
     * The tenant end points are searched for matching selectors and tenant app types to get the right component
     * based on @TCITenantComponent annotation or @Input interface
     * @return @Observable<@ViewRef>
     * @deprecated
     */
    // private oldloadDynamicComponent(): Observable<ViewRef> {
    //     // let _modFactory: NgModuleFactory<any>;
    //     const this = this;
    //     return this
    //         .tenantRemoteServices
    //         .getRemoteServices()
    //         .flatMap(svc => svc)
    //         .concat(LOCAL_TENANT_SVCS)
    //         .filter((tenant: IService) => {
    //             const cfg = (<any>SystemJS).getConfig();
    //             const appType = TCITenantAppType.fromString(tenant.name);
    //             const svcName = appType.getSvcName(); // TODO: Flogo temporal switch
    //             return cfg.map && cfg.packages && cfg.map[svcName];
    //         })
    //         .map((tenant: IService) =>
    //             /**
    //              * The tenant names are from tci-deployer i.e. "wistudio" and "scribe"
    //              *  and we are getting the tenant names from backend API /api/v1/uiservices as they match the deployer names
    //              *  We are banking on the moduleconfig.json map + package entry
    //              *  where the tenant name suffixed with a "-svc" is mapped to the module e.g.
    //              *  map:
    //              *      "wistudio-svc" :  : "wi-host:",
    //              *  package:
    //              *      "wistudio-svc" : { "main": "app/app.child.module.js", "defaultExtension": "js" },
    //              *
    //              * NOTE that this entry is different from the app entry point e..g.
    //              * map:
    //              *      "wi-studio/client" : "wi-host:",
    //              *      "wi-studio/client": "wi-host:",
    //              *      "wi-studio/index": "wi-host:",
    //              *  package:
    //              *      "wi-studio/client": { "main": "app/app.child.module.js", "defaultExtension": "js" },
    //              *      "wi-studio/index": { "main": "index.js", "defaultExtension": "js" },
    //              *      "wi-studio": { "main": "index.js", "defaultExtension": "js" },
    //              *
    //              */
    //             Observable.fromPromise(this.loader.load(TCITenantAppType.fromString(tenant.name).getSvcName()))
    //         )
    //         .flatMap(modFac => modFac)
    //         .map(modFactory => <Observable<ITCIResFactory<any, any>[]>>Observable.create(obs => {
    //             Observable
    //                 .fromPromise(this.compiler.compileModuleAndAllComponentsAsync<any>(modFactory.moduleType))
    //                 .map(mwcfactories => mwcfactories.componentFactories)
    //                 .flatMap(factories => factories)
    //                 .filter(factory => this
    //                     .getComponentSelector() && this
    //                     .getComponentSelector()
    //                     .isEqualTo(factory.selector))
    //                 .filter(factory => factory.componentType.prototype
    //                     instanceof AbstractTCITenantComponent)
    //                 .filter(factory => factory
    //                         .componentType
    //                         .prototype
    //                         .getAnnotations() &&
    //                     factory
    //                         .componentType
    //                         .prototype
    //                         .getAnnotations()
    //                         .tenantAppType
    //                         .equals(this.getTenantAppType().toString()))
    //                 .distinct(factory => {
    //                     if (
    //                         factory
    //                             .componentType
    //                             .prototype
    //                             .getAnnotations() &&
    //                         factory
    //                             .componentType
    //                             .prototype
    //                             .getAnnotations()
    //                             .distinctionKeyFn
    //                     ) {
    //                         return factory
    //                             .componentType
    //                             .prototype
    //                             .getAnnotations()
    //                             .distinctionKeyFn(this.input);
    //                     } else {
    //                         return factory;
    //                     }
    //                 })
    //                 .map(factory => {
    //                     const res = <ITCIResFactory<any, any>>{modFactory: modFactory};
    //                     if (factory
    //                             .componentType
    //                             .prototype
    //                             .getAnnotations() &&
    //                         factory
    //                             .componentType
    //                             .prototype
    //                             .getAnnotations()
    //                             .tenantAppType
    //                             .equals(this.getTenantAppType().toString()
    //                             )
    //                     ) {
    //                         res.cmpFactory = factory;
    //                     }
    //                     return res;
    //                 })
    //                 .toArray()
    //                 .subscribe(res => {
    //                     obs.next(<ITCIResFactory<any, any>[]>res);
    //                     obs.complete();
    //                 });
    //         }))
    //         .flatMap(res => res)
    //         .map(res => {
    //             const data = res.filter(fact => <any>fact.modFactory && <any>fact.cmpFactory)
    //                 .pop();
    //             if (data && data.modFactory && data.cmpFactory) {
    //                 const modRef = data.modFactory.create(this.getInjector());
    //                 this.cmpRef = this.viewref.createComponent(data.cmpFactory, 0, modRef.injector);
    //                 if (this.cmpRef.instance && this.cmpRef.instance instanceof AbstractTCITenantComponent) {
    //                     const thisInst: AbstractTCITenantComponent<E, I, O> = this.cmpRef.instance;
    //                     thisInst.input = this.input;
    //                     thisInst.output = this.output;
    //                     return this.viewref.insert(this.cmpRef.hostView);
    //                 } else {
    //                     throw new Error("Component does not exist or does not implement AbstractNgRemoteComponent abstract class");
    //                 }
    //             } else {
    //                 return null;
    //             }
    //         })
    //         .toArray()
    //         .map(res => {
    //             const data = res.filter(d => d !== null);
    //
    //             if (data.length === 1) {
    //                 return data.pop();
    //             }
    //
    //             // need to remove any loaded component
    //             this.viewref.clear();
    //             if (data.length > 1) {
    //                 throw new Error("More than one component available for this tenant");
    //             } else if (data.length < 1) {
    //                 throw new Error("No component available for this tenant");
    //             }
    //         });
    // }

    private loadComponent(): Observable<ViewRef> {
        return Observable.fromPromise(this.loader.load(this.getComponentModulePath()))
            .map(modFactory => <Observable<ITCIResFactory<any, any>[]>>Observable.create(obs => {
                Observable.fromPromise(this.compiler.compileModuleAndAllComponentsAsync<any>(modFactory.moduleType))
                    .map(mwcfactories => mwcfactories.componentFactories)
                    .flatMap(factories => factories)
                    .map(factory => {
                        const res = <ITCIResFactory<any, any>>{modFactory: modFactory};
                        if (
                            this.getComponentSelector()
                                .isEqualTo(factory.selector)
                            && factory.componentType.prototype instanceof AbstractTCITenantComponent
                            && factory.componentType.prototype.getAnnotations()
                                .tenantAppType
                                .equals(this.getTenantAppType()
                                    .toString())
                        ) {
                            res.cmpFactory = factory;
                        }
                        return res;
                    })
                    .toArray()
                    .subscribe(res => {
                        obs.next(<ITCIResFactory<any, any>[]>res);
                        obs.complete();
                    });
            }))
            .flatMap(res => res)
            .map(res => {
                const data = res.filter(fact => <any>fact.modFactory && <any>fact.cmpFactory)
                    .pop();
                if (data && data.modFactory && data.cmpFactory) {
                    const modRef = data.modFactory.create(this.getInjector());
                    this.viewref.clear();
                    this.cmpRef = this.viewref.createComponent(data.cmpFactory, 0, modRef.injector);
                    const thisInst: AbstractTCITenantComponent<E, I, O> = this.cmpRef.instance;
                    if (this.cmpRef.instance && this.cmpRef.instance instanceof AbstractTCITenantComponent) {
                        thisInst.input = this.input;
                        thisInst.output = this.output;
                        return this.viewref.insert(this.cmpRef.hostView);
                    } else {
                        throw new Error("Component does not exist or does not implement AbstractNgRemoteComponent abstract class");
                    }
                } else {
                    throw new Error("No component available for this tenant");
                }
            });
    }

    /**
     * Returns the injector
     * @protected
     * @return {Injector}
     */
    protected getInjector(): Injector {
        return this.componentInjector;
    }

    protected getResolvedProviders(): ResolvedReflectiveProvider[] {
        return this.resolvedProviders;
    }

    /**
     * get annotation from metadata
     * @protected
     */
    protected getAnnotations(): ITCITenantComponentLoaderMetadata {
        return <ITCITenantComponentLoaderMetadata>Reflect.getMetadata(
            "custom:annotation",
            this.constructor,
            TCI_TENANT_COMPONENT_LOADER_SYMBOL
        );
    }

    getTenantAppType(): TCITenantAppType {
        const metadata = this.getAnnotations();
        if (metadata && !this.getDynamic()) {
            return metadata.tenantAppType;
        } else {
            return this.input.tenantAppType;
        }
    }

    getComponentSelector(): TCITenantSelectorType {
        const metadata = this.getAnnotations();
        if (metadata && !this.getDynamic()) {
            return metadata.componentSelector;
        } else {
            return this.input.componentSelector;
        }
    }

    /**
     * Returns a <module path>#<ModuleClassName>
     * e.g. wi-studio/client/app/flowslist/flowsList.module#FlowsListModule
     */
    getComponentModulePath(): string {
        const metadata = this.getAnnotations();
        if (metadata && !this.getDynamic()) {
            return `${metadata.moduleRoute}#${metadata.moduleClass}`;
        } else {
            return ``;
        }
    }

    /**
     * Returns true if dynamic component is to be loaded
     */
    getDynamic(): boolean {
        const metadata = this.getAnnotations();
        return metadata ? metadata.isdynamic : this.isDynamic();
    }

    protected isDynamic(): boolean {
        return false;
    }

    getMountedComponent() {
        return this.cmpRef.instance;
    }
}
